import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { useRouter } from 'next/router';
import { IRS_RAIL_BUTTON_LIST, tenorToYears } from 'lib/constants';
import { socket, socketConnect, socketEmit } from 'services/socket';
import useIrsContextStore from 'store/irsContextStore';
import SidebarSection from '@/molecules/RailButton';
import CommandHistoryPanel from 'organisms/CommandHistoryPanel';
import { DataContext } from '@/components/context/DataContext';
import parsePlot, { getEndTenorLegsFromParsedPlot } from 'lib/getFormattedLegs';

const RailSideBar = () => {
    const router = useRouter();
    const context = useIrsContextStore((state) => state);
    const { setActiveRatesCcy } = useContext(DataContext);
    const {
        id, command: commandFromQuery, advanced = false, legs, depId, l1 = null, indepId = null, l2 = null, str = null, depF = null, indepF = null, immFwd, immLabel, depImm, indepImm, meetingDate, plotCommand, plotType, bobCurve = null, basis6s3sCurve = null, timePeriod, startDate, endDate, range, imm, immLabel3
    } = router.query;
    const shouldShowRail = ['/plot/irs/[id]', '/regress', '/anchor', '/chronos/[id]', '/arc/irs/[id]', '/cta', '/edge/irs/[id]', '/plotf/irs/[id]', '/stats/irs/[id]'].includes(router.pathname) && !bobCurve && !basis6s3sCurve;

    const sections = useMemo(() => IRS_RAIL_BUTTON_LIST ?? [], []);
    const [referenceIrs, setReferenceIrs] = useState(null);
    const [resolvedSecurityId, setResolvedSecurityId] = useState(null);
    const [irs, setIrs] = useState(null);
    const [defaultId, setDefaultId] = useState(null);
    const [validImm, setValidImm] = useState([]);
    const activeCommand = useMemo(() => {
        if (router.pathname === '/cta') return 'CTASCAN';
        if (router.pathname === '/regress') return 'REGRESS';
        if (router.pathname === '/anchor') return 'ANCHOR';
        if (router.pathname?.startsWith('/arc/irs/')) return 'ARC';
        if (router.pathname?.startsWith('/edge/irs/')) return 'EDGE';
        if (router.pathname?.startsWith('/chronos')) return 'TRACE';
        if (router.pathname?.startsWith('/plot/irs/')) {
            const adv = Array.isArray(advanced) ? advanced[0] : advanced;
            if (adv === 'true' || adv === true) return 'PLOTA';
            if (commandFromQuery?.toLowerCase() === 'basis') return 'BASIS';
            return 'PLOT';
        }
        if (router.pathname?.startsWith('/plotf/irs/')) return 'PLOTF';
        if (router.pathname?.startsWith('/stats/irs/')) return 'STATS';
        return commandFromQuery || '';
    }, [router.pathname, commandFromQuery, advanced]);

    const activeCommandUpper = useMemo(
        () => String(activeCommand || '').toUpperCase(),
        [activeCommand]
    );

    const plotId = context.dependentCurveId ?? id ?? depId ?? indepId ?? defaultId;
    const securityId = resolvedSecurityId ?? plotId;
    const plotLegs = legs ?? l1 ?? l2 ?? str ?? '5y';
    const plotStr = str ?? legs ?? l1 ?? l2;
    const isFwd = commandFromQuery === 'fwcm' || plotCommand === 'fwcm' || depF === '1';
    const isImm = Boolean(immFwd) || depImm === '1';

    const onIrsEvent = (data) => {
        if (data?.error) {
            return;
        }
        setIrs(data);
    };

    const onGetIrsSecurity = (data) => {
        if (data?.error) return;
        if (data?.id) setResolvedSecurityId(String(data.id));
    };
    const onGetIrsSecurityByIrsId = (data) => {
        if (data?.error) return;
        if (data?.id) setResolvedSecurityId(String(data.id));
    };

    const onAllIrsEvent = (data) => {
        if (!Array.isArray(data)) return;
        const extractId = (item) => item?.id;
        const usdSofr = data.find((item) => item?.currency === 'USD');
        const resolvedId = extractId(usdSofr);
        if (resolvedId) setDefaultId(resolvedId);
    };

    const onGetImm = ({ data, err, label }) => {
        if (err || label) return;
        setValidImm(data);
    };

    useEffect(() => {
        socketConnect();
        socket.on('/irs/reference', setReferenceIrs);
        socket.on('/get/irs-securities/:id', onGetIrsSecurity);
        socket.on('/get/irs-securities/:irsId', onGetIrsSecurityByIrsId);
        socket.on('/plots/irs/:id', onIrsEvent);
        socket.on('/get/irs', onAllIrsEvent);
        socket.on('/get/imm/valid', onGetImm);
        socketEmit('/irs/reference');
        socketEmit('/get/irs');
        socketEmit('/get/imm', { date: new Date(), n: 25, discardExpired: true });

        return () => {
            socket.off('/get/imm/valid', onGetImm);
            socket.off('/irs/reference', setReferenceIrs);
            socket.off('/get/irs-securities/:id', onGetIrsSecurity);
            socket.off('/get/irs-securities/:irsId', onGetIrsSecurityByIrsId);
            socket.off('/plots/irs/:id', onIrsEvent);
            socket.off('/get/irs', onAllIrsEvent);
        };
    }, []);

    useEffect(() => {
        if (!router.isReady) return;

        if (plotId) {
            const raw = (legs ?? l1 ?? str ?? '').toString();
            const term = raw
                .split(',')
                .map((t) => t.trim().toUpperCase().replace(/^(\d+(?:\.\d+)?)([A-Za-z])$/, '$1 $2'))
                .join(',');
            if (!term) return;
            socketEmit('/get/irs-securities/:irsId', { irsId: id, term });
            socketEmit('/plots/irs/:id', { id: plotId });
        }
    }, [router.isReady, router.pathname, id, plotId, legs, l1, str]);

    const normalizeLegsParam = useCallback((raw) => {
        const normalized = String(raw ?? '').trim().toLowerCase();
        if (!normalized) return '5y';
        const isValid = (val) => /^\d+(?:\.\d+)?[dwmy]$/i.test(val);
        if (
            normalized.includes(',')
            && !normalized.includes('*')
            && !normalized.includes('-')
        ) {
            const parts = normalized
                .split(',')
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p) => (isValid(p) ? p : '5y'));

            return parts.length ? parts.join(',') : '5y';
        }

        // Single value
        return isValid(normalized) ? normalized : '5y';
    }, []);

    const getEndTenorLegsFromQuery = useCallback((rawLegs, forwardLike) => {
        const getTenorRank = (tenor) => Number(tenorToYears(tenor)) || 0;
        const legsList = String(rawLegs ?? '')
            .split(',')
            .map((v) => v.trim().toLowerCase())
            .filter((v) => /^\d+(?:\.\d+)?[dwmy]$/i.test(v));

        if (!legsList.length) return null;

        if (!forwardLike) {
            return legsList
                .sort((a, b) => getTenorRank(a) - getTenorRank(b))
                .join(',');
        }

        const oddIndexLegs = legsList.filter((_, idx) => idx % 2 === 1);
        if (oddIndexLegs.length) {
            return oddIndexLegs
                .sort((a, b) => getTenorRank(a) - getTenorRank(b))
                .join(',');
        }

        const endLegs = [];
        for (let i = 0; i < legsList.length; i += 2) {
            const pair = legsList.slice(i, i + 2);
            if (pair.length === 1) {
                endLegs.push(pair[0]);
                continue;
            }
            const [a, b] = pair;
            endLegs.push(getTenorRank(a) >= getTenorRank(b) ? a : b);
        }

        return endLegs
            .sort((a, b) => getTenorRank(a) - getTenorRank(b))
            .join(',');
    }, []);

    const sharedTimeQuery = useMemo(() => {
        const selectedTimePeriod = timePeriod ?? range ?? null;
        return {
            ...(selectedTimePeriod ? { timePeriod: selectedTimePeriod } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {})
        };
    }, [timePeriod, range, startDate, endDate]);

    const buildNextPlotQuery = useCallback(({ advanced: nextAdvanced = false, overrides = {} } = {}) => {
        const next = { ...router.query };
        ['depId', 'indepId', 'l1', 'l2', 'depF', 'indepF', 'depImm', 'indepImm', 'id', 's2', 'parsedLegs', 'str', 'plotaSeries', 'plotfSeries'].forEach((k) => {
            delete next[k];
        });
        const hasOverride = (key) => Object.prototype.hasOwnProperty.call(overrides, key);

        const legsParam = normalizeLegsParam(overrides.plotLegs ?? next.legs ?? plotLegs ?? plotStr);
        if (legsParam) next.legs = legsParam;

        const nextIsFwd = hasOverride('isFwd') ? Boolean(overrides.isFwd) : (isFwd || next.command === 'fwcm');
        if (nextIsFwd) next.command = 'fwcm';
        else delete next.command;

        if (hasOverride('plotType')) next.plotType = overrides.plotType;
        else delete next.plotType;

        const nextIsImm = hasOverride('isImm') ? Boolean(overrides.isImm) : Boolean(immFwd);
        const nextImmFwd = hasOverride('immFwd') ? overrides.immFwd : immFwd;
        const nextImmLabel = hasOverride('immLabel') ? overrides.immLabel : immLabel;
        const nextImm = hasOverride('imm') ? overrides.imm : imm;
        const nextImmLabel3 = hasOverride('immLabel3') ? overrides.immLabel3 : immLabel3;
        if (nextIsImm && nextImmFwd) next.immFwd = nextImmFwd;
        else delete next.immFwd;
        if (nextIsImm && nextImmLabel) next.immLabel = nextImmLabel;
        else delete next.immLabel;
        if (nextIsImm && nextImm) next.imm = nextImm;
        else delete next.imm;
        if (nextIsImm && nextImmLabel3) next.immLabel3 = nextImmLabel3;
        else delete next.immLabel3;
        if (meetingDate) next.meetingDate = meetingDate;
        if (timePeriod) next.timePeriod = timePeriod;
        if (startDate) next.startDate = startDate;
        if (endDate) next.endDate = endDate;

        if (nextAdvanced) next.advanced = 'true';
        else delete next.advanced;

        return next;
    }, [router.query, normalizeLegsParam, plotLegs, plotStr, isFwd, immFwd, immLabel, imm, immLabel3, meetingDate]);

    const [historyPanel, setHistoryPanel] = useState({ open: false, tab: 'recent', token: 0 });

    const closeHistoryPanel = useCallback(() => setHistoryPanel((p) => ({ ...p, open: false })), []);

    const handleRailButtonClick = useCallback((nextCommandRaw) => {
        const nextCommand = String(nextCommandRaw || '').toUpperCase();
        if (!nextCommand) return;

        // HIST/FAVS toggle the shared panel for their own tab: clicking the
        // already-open tab closes it; clicking the other tab switches to it.
        if (nextCommand === 'HIST') {
            setHistoryPanel((p) => (p.open && p.tab === 'recent'
                ? { ...p, open: false }
                : { open: true, tab: 'recent', token: p.token + 1 }));
            return;
        }
        if (nextCommand === 'FAVS') {
            setHistoryPanel((p) => (p.open && p.tab === 'favourites'
                ? { ...p, open: false }
                : { open: true, tab: 'favourites', token: p.token + 1 }));
            return;
        }

        if (activeCommandUpper === nextCommand) return;

        let plotLegsOverride = plotLegs;
        let isFwdOverride = isFwd;
        let isImmOverride = isImm;
        let immFwdOverride = immFwd;
        let immLabelOverride = immLabel;
        let immOverride = imm;
        let immLabel3Override = immLabel3;
        let newPlotType = plotType;

        const parsedPlot = parsePlot(`plot ${context.structure1}`, validImm, true);
        if (parsedPlot?.isValid) {
            const {
                tenors, isFwd: parsedIsFwd, imm, secondImm, plotType, thirdImm
            } = parsedPlot;
            if (tenors) plotLegsOverride = tenors;
            isFwdOverride = Boolean(parsedIsFwd);
            isImmOverride = Boolean(imm);
            if (imm?.label) immFwdOverride = imm.label;
            else immFwdOverride = null;
            if (secondImm?.label) immLabelOverride = secondImm.label;
            else immLabelOverride = null;
            if (secondImm?.y) immOverride = secondImm.y;
            else immOverride = null;
            if (thirdImm?.label) immLabel3Override = thirdImm.label;
            else immLabel3Override = null;
            newPlotType = plotType;
        }
        const effectivePlotLegs = plotLegsOverride ?? plotLegs;
        const effectiveIsFwd = Boolean(isFwdOverride);
        const effectiveIsImm = Boolean(isImmOverride);
        const effectiveImmFwd = effectiveIsImm ? immFwdOverride : null;
        const effectiveImmLabel = effectiveIsImm ? immLabelOverride : null;
        const effectiveImm = effectiveIsImm ? immOverride : null;
        const effectiveImmLabel3 = effectiveIsImm ? immLabel3Override : null;

        switch (nextCommand) {
            case 'TRACE': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                router.push({
                    pathname: `/chronos/${plotId}`,
                    query: {
                        ...(str && { str }),
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(effectiveIsFwd ? { command: 'fwcm' } : {}),
                        ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                });
                return;
            }
            case 'REGRESS': {
                const dep = plotId ?? depId ?? id;
                const s1Leg = String(context.regressStr1 ?? '').trim();
                const s2 = normalizeLegsParam(effectivePlotLegs ?? plotStr) ?? '5y';
                if (!dep || !s2) return;
                const timeQueryString = [
                    sharedTimeQuery.timePeriod ? `&timePeriod=${sharedTimeQuery.timePeriod}` : '',
                    sharedTimeQuery.startDate ? `&startDate=${sharedTimeQuery.startDate}` : '',
                    sharedTimeQuery.endDate ? `&endDate=${sharedTimeQuery.endDate}` : ''
                ].join('');
                router.push(
                    `/regress?depId=${dep}&indepId=${dep}&l2=${s2}${effectiveIsFwd ? '&indepF=1' : ''}${effectiveIsImm ? `&indepImm=1${effectiveImmFwd ? `&immFwd=${effectiveImmFwd}` : ''}` : ''}${meetingDate ? `&meetingDate=${meetingDate}` : ''}&s2=1${effectiveImmLabel ? `&immLabel=${effectiveImmLabel}` : ''}${newPlotType ? `&plotType=${newPlotType}` : ''}${timeQueryString}${s1Leg ? `&s1Leg=${s1Leg}` : ''}`
                    + `${effectiveImm ? `&imm=${effectiveImm}` : ''}${effectiveImmLabel3 ? `&immLabel3=${effectiveImmLabel3}` : ''}`
                );
                return;
            }
            case 'ANCHOR': {
                const dep = plotId ?? depId ?? id;
                const indep = indepId ?? '';
                const s1 = (effectivePlotLegs ?? l1 ?? legs ?? str ?? l2) ?? '5y';
                if (!dep || !s1) return;
                const timeQueryString = [
                    sharedTimeQuery.timePeriod ? `&timePeriod=${sharedTimeQuery.timePeriod}` : '',
                    sharedTimeQuery.startDate ? `&startDate=${sharedTimeQuery.startDate}` : '',
                    sharedTimeQuery.endDate ? `&endDate=${sharedTimeQuery.endDate}` : ''
                ].join('');
                router.push(
                    `/anchor?l1=${s1}&depId=${dep}&indepId=${dep}${effectiveIsFwd ? '&depF=1' : ''}${effectiveIsImm ? `&depImm=1${effectiveImmFwd ? `&immFwd=${effectiveImmFwd}` : ''}` : ''}${meetingDate ? `&meetingDate=${meetingDate}` : ''}${effectiveImmLabel ? `&immLabel=${effectiveImmLabel}` : ''}${newPlotType ? `&plotType=${newPlotType}` : ''}${timeQueryString}`
                    + `${effectiveImm ? `&imm=${effectiveImm}` : ''}${effectiveImmLabel3 ? `&immLabel3=${effectiveImmLabel3}` : ''}`
                );
                return;
            }
            case 'ARC': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                router.push({
                    pathname: `/arc/irs/${plotId}`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(effectiveIsFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                });
                return;
            }
            case 'PLOT': {
                if (!plotId) return;
                router.push(
                    {
                        pathname: `/plot/irs/${plotId}`,
                        query: buildNextPlotQuery({
                            advanced: false,
                            overrides: {
                                plotLegs: plotLegsOverride,
                                isFwd: isFwdOverride,
                                isImm: isImmOverride,
                                immFwd: immFwdOverride,
                                immLabel: immLabelOverride,
                                imm: immOverride,
                                immLabel3: immLabel3Override,
                                plotType: newPlotType
                            }
                        })
                    },
                    undefined,
                    { shallow: false }
                );
                return;
            }
            case 'PLOTA': {
                if (!plotId) return;
                router.push(
                    {
                        pathname: `/plot/irs/${plotId}`,
                        query: buildNextPlotQuery({
                            advanced: true,
                            overrides: {
                                plotLegs: plotLegsOverride,
                                isFwd: isFwdOverride,
                                isImm: isImmOverride,
                                immFwd: immFwdOverride,
                                immLabel: immLabelOverride,
                                imm: immOverride,
                                immLabel3: immLabel3Override,
                                plotType: newPlotType
                            }
                        })
                    },
                    undefined,
                    { shallow: false }
                );
                return;
            }
            case 'BASIS': {
                const dep = plotId ?? depId;
                const refId = referenceIrs?.data?.id ?? referenceIrs?.id ?? referenceIrs?.value?.id;
                const indep = refId ?? plotId ?? indepId;
                const basisLegs = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                if (!dep || !indep || !basisLegs) return;

                router.push({
                    pathname: `/plot/irs/${plotId}`,
                    query: {
                        command: 'BASIS',
                        depId: dep,
                        indepId: indep,
                        l1: basisLegs,
                        l2: basisLegs,
                        ...(effectiveIsFwd ? { depF: '1', indepF: '1' } : {}),
                        ...(effectiveIsImm ? { depImm: '1', indepImm: '1', ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}) } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                }, undefined, { shallow: false });
                return;
            }
            case 'EDGE': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                router.push({
                    pathname: `/edge/irs/${plotId}`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(effectiveIsFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                });
                return;
            }
            case 'CTASCAN': {
                if (!plotId || !irs) return;
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                setActiveRatesCcy(irs?.currency);
                router.push({
                    pathname: '/cta',
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        id: plotId,
                        ...(effectiveIsFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                });
                return;
            }
            case 'PLOTF': {
                if (!plotId) return;
                const isForwardLike = effectiveIsFwd || commandFromQuery === 'fwcm' || plotCommand === 'fwcm' || depF === '1' || indepF === '1';
                const endTenorLegs = parsedPlot?.isValid ? getEndTenorLegsFromParsedPlot(parsedPlot) : getEndTenorLegsFromQuery(plotLegs ?? plotStr, isForwardLike);
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                router.push(
                    {
                        pathname: `/plotf/irs/${plotId}`,
                        query: {
                            ...(meetingDate && { str: meetingDate }),
                            ...(endTenorLegs && { parsedLegs: endTenorLegs }),
                            legs: legsParam,
                            ...(effectiveIsFwd ? { plotCommand: 'fwcm' } : {}),
                            ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                            ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                            ...(effectiveImm ? { imm: effectiveImm } : {}),
                            ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                            ...(meetingDate ? { meetingDate } : {}),
                            ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                            ...(newPlotType ? { plotType: newPlotType } : {}),
                            ...sharedTimeQuery,
                            ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                        }
                    },
                    undefined,
                    { shallow: false }
                );
                return;
            }
            case 'STATS': {
                if (!plotId) return;
                const legsParam = normalizeLegsParam(effectivePlotLegs ?? plotStr);
                router.push({
                    pathname: `/stats/irs/${plotId}`,
                    query: {
                        ...(meetingDate && { str: meetingDate }),
                        legs: legsParam,
                        ...(effectiveIsFwd ? { plotCommand: 'fwcm' } : {}),
                        ...(effectiveImmFwd ? { immFwd: effectiveImmFwd } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(effectiveImm ? { imm: effectiveImm } : {}),
                        ...(effectiveImmLabel3 ? { immLabel3: effectiveImmLabel3 } : {}),
                        ...(meetingDate ? { meetingDate } : {}),
                        ...(effectiveImmLabel ? { immLabel: effectiveImmLabel } : {}),
                        ...(newPlotType ? { plotType: newPlotType } : {}),
                        ...sharedTimeQuery,
                        ...(sharedTimeQuery.timePeriod ? { range: sharedTimeQuery.timePeriod } : {})
                    }
                });
            }
            default:
        }
    }, [
        activeCommandUpper,
        buildNextPlotQuery,
        commandFromQuery,
        depF,
        depId,
        id,
        indepF,
        indepId,
        isFwd,
        isImm,
        l1,
        l2,
        legs,
        normalizeLegsParam,
        getEndTenorLegsFromQuery,
        plotId,
        plotLegs,
        plotStr,
        router,
        str,
        referenceIrs,
        securityId,
        irs,
        defaultId,
        isFwd,
        sharedTimeQuery,
        context,
        validImm,
        imm,
        immLabel3
    ]);

    // The page command keeps its highlight; HIST or FAVS lights up additively
    // while the history panel is open on the matching tab.
    const activeRailCommands = useMemo(() => {
        const list = [activeCommandUpper];
        if (historyPanel.open) list.push(historyPanel.tab === 'favourites' ? 'FAVS' : 'HIST');
        return list;
    }, [activeCommandUpper, historyPanel.open, historyPanel.tab]);

    if (!shouldShowRail) return <></>;

    return (
        <>
        <div className="flex h-screen relative">
            <div className="flex flex-col px-3 gap-2.5 border-r border-gray-600 h-screen">
                <div className='flex justify-center items-center pt-7 pb-2'>
                    <img src='/images/icons/green-bird.svg' alt="Green Bird" width={45} />
                </div>
                {sections.map((item) => (
                    <SidebarSection
                        key={item.command || item.name}
                        name={item.name}
                        title={item.section}
                        command={item.command}
                        items={item.items}
                        hoverText={item.hoverText}
                        active={activeRailCommands}
                        onSelect={handleRailButtonClick}
                    />
                ))}
            </div>
            <CommandHistoryPanel
                isOpen={historyPanel.open}
                initialTab={historyPanel.tab}
                openToken={historyPanel.token}
                onClose={closeHistoryPanel}
            />
        </div>
        </>
    );
};

export default RailSideBar;
